// SAB-430: Section J — rich marketing footer.
//
// Includes a "Manage cookie preferences" link in the bottom row, wired to
// resetConsent() from src/lib/consent — required for SAB-431 compliance.
// The design markup doesn't show this button, but adding it is a consent
// requirement, not a design deviation. The global Footer.tsx is suppressed
// on `/` (it has its own useLocation guard) so this is the only footer
// rendering on the marketing homepage.
//
// Legal links point to the real /terms, /privacy, /responsible-gaming,
// /state-availability, /cookies routes. As of SAB-522 all of these render
// in-app (/terms + /cookies were 404, /privacy blank-redirected to Termly).
import { Link } from 'react-router-dom';
import { resetConsent } from '@/lib/consent';
import { usePlatformContext } from '@/hooks/usePlatformContext';
import { PaymentMarks } from '@/components/PaymentMarks';

interface LinkSpec { label: string; href: string; }

const PLAY_LINKS: LinkSpec[] = [
  { label: 'Browse Leagues',  href: '/nfl/leagues/browse' },
  { label: 'Create a League', href: '/nfl/create' },
  { label: 'Join a League',   href: '/nfl/leagues/browse' },
  { label: 'How it works',    href: '#how' },
];

const COMPANY_LINKS: LinkSpec[] = [
  // About removed until a real /about page exists (Adam has a draft in progress) -
  // better no link than a dead href on a launch page. Build About page later.
  { label: 'Press',   href: 'mailto:press@sabotagefantasy.com' },
  { label: 'Contact', href: '/support' },
  // SAB-706: Blog link removed. The WordPress blog (/blog → Vercel rewrite to
  // Kinsta) carries a cookie-consent banner that, reachable in-app, triggered the
  // App Store 5.1.2(i) rejection. The blog is unfinished with no launch dependency,
  // so it is removed rather than conditionally suppressed.
];

const LEGAL_LINKS: LinkSpec[] = [
  { label: 'Terms of Service',    href: '/terms' },
  { label: 'Privacy Policy',      href: '/privacy' },
  { label: 'Responsible Gaming',  href: '/responsible-gaming' },
  { label: 'How Withdrawals Work', href: '/withdrawals' },
  { label: 'State Availability',  href: '/state-availability' },
  { label: 'Cookie Policy',       href: '/cookies' },
];

function FooterLink({ link }: { link: LinkSpec }) {
  // Anchor (#how), external (http), and mailto: → <a>. Internal routes → <Link>.
  if (link.href.startsWith('#') || link.href.startsWith('http') || link.href.startsWith('mailto:')) {
    return (
      <a href={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
      {link.label}
    </Link>
  );
}

export function MarketingFooter() {
  // 2.3.10: never render the Google Play badge inside the iOS app (same isIosApp signal
  // DownloadLinks uses). The App Store badge stays; only the Android CTA is gated.
  const { isIosApp } = usePlatformContext();
  return (
    <footer className="border-t border-border bg-background">
      {/* SAB-445: section padding pt-14 pb-8 → pt-10 pb-6 on mobile. */}
      <div className="mx-auto max-w-[1180px] px-6 pt-10 pb-6 sm:pt-14 sm:pb-8">
        {/* Sportradar attribution — SAB-445: tighter spacing on mobile */}
        <div className="flex justify-center pb-6 mb-6 sm:pb-9 sm:mb-9 border-b border-border">
          <img
            src="/badges/sportradar-powered-by.png"
            alt="Powered by Sportradar"
            className="h-6 w-auto opacity-85"
          />
        </div>

        {/* 4-col grid
            SAB-445 (carve-out: NO accordion — compliance/trust requires the
            helplines, legal links and age line all stay visible without an
            interaction): explicit 2-col on mobile so Play/Company/Legal sit
            side-by-side instead of stacking 1-col with 44px-tap-target
            inflation. ~600px saved. Tighter gap on mobile. */}
        <div
          className="grid gap-6 sm:gap-9 mb-8 sm:mb-10 grid-cols-2 sm:[grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]"
        >
          {/* Brand — SAB-445: span both columns on mobile so the tagline +
              app badges get a full row, then Play/Company sit side-by-side
              in row 2 and Legal alone in row 3. Single column on desktop. */}
          <div className="col-span-2 sm:col-span-1">
            <img
              src="/images/logo-topbar@2x.png"
              alt="Sabotage Fantasy"
              className="h-[34px] mb-3.5"
            />
            <p className="text-[13px] text-muted-foreground max-w-[240px] m-0">
              Where failure wins. Season-long fantasy with inverted scoring and sabotage cards.
            </p>
            <div className="flex gap-2.5 mt-4">
              <a
                href="https://apps.apple.com/us/app/sabotage-fantasy/id6760446288"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
              >
                <img
                  src="/badges/app-store-badge.svg"
                  alt="Download on the App Store"
                  className="h-[38px]"
                />
              </a>
              {!isIosApp && (
                <a
                  href="https://play.google.com/store/apps/details?id=com.sabotagefantasy.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <img
                    src="/badges/google-play-badge.svg"
                    alt="Get it on Google Play"
                    className="h-[38px]"
                  />
                </a>
              )}
            </div>
          </div>

          {/* Play */}
          <div>
            <div className="font-display font-bold text-[13px] uppercase tracking-[0.06em] text-foreground mb-3.5">
              Play
            </div>
            <div className="flex flex-col gap-2.5">
              {PLAY_LINKS.map((l) => <FooterLink key={l.label} link={l} />)}
            </div>
          </div>

          {/* Company */}
          <div>
            <div className="font-display font-bold text-[13px] uppercase tracking-[0.06em] text-foreground mb-3.5">
              Company
            </div>
            <div className="flex flex-col gap-2.5">
              {COMPANY_LINKS.map((l) => <FooterLink key={l.label} link={l} />)}
            </div>
          </div>

          {/* Legal */}
          <div>
            <div className="font-display font-bold text-[13px] uppercase tracking-[0.06em] text-foreground mb-3.5">
              Legal
            </div>
            <div className="flex flex-col gap-2.5">
              {LEGAL_LINKS.map((l) => <FooterLink key={l.label} link={l} />)}
            </div>
          </div>
        </div>

        {/* Payments — card-brand acceptance marks (Soap card-brand review) */}
        <div className="border-t border-border pt-6 pb-6 flex items-center gap-4 flex-wrap">
          {/* SAB-722: these are DEPOSIT acceptance marks. Payouts are Visa/Mastercard
              only (no Amex), so the row is labelled "Deposits:" to stop a reader
              taking three marks as three payout brands. The full distinction lives on
              /withdrawals, linked from the Legal column above — not repeated here. */}
          <span className="text-[13px] text-muted-foreground">Deposits:</span>
          <PaymentMarks size="md" />
          <span className="text-[13px] text-muted-foreground">Payments processed by Soap</span>
        </div>

        {/* Bottom legal */}
        <div className="border-t border-border pt-6 flex flex-col gap-3.5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[13px] text-muted-foreground">
              Must be 18+ (19+ in Nebraska). Responsible gaming: call or text{' '}
              <b className="text-foreground">1-800-GAMBLER</b> or visit{' '}
              <a
                href="https://www.ncpgambling.org"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-foreground hover:text-primary"
              >
                ncpgambling.org
              </a>.
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-muted-foreground/80 m-0">
              © 2026 Sabotage Fantasy. EST. 2026. Must be of legal age and
              physically located in an eligible state. Void where prohibited. Play responsibly.
            </p>
            <button
              type="button"
              onClick={resetConsent}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
            >
              Manage cookie preferences
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default MarketingFooter;
